import React, { useState, useRef } from 'react';
import { Check, Edit3 } from 'lucide-react';
import { getPosition, getGranularity } from '@/shared/logic/timeline-logic';
import { EVENT_THEMES, EVENT_TYPES } from '@/shared/constants/event-types.v4';

interface TimelineDraftBlockProps {
    start: Date;
    end: Date;
    selectedDate: Date;
    hourHeight: number;
    onUpdate: (updates: { start?: Date; end?: Date }) => void;
    onConfirm: (title: string) => void;
    onCancel: () => void;
    onExpand: (title: string) => void;
}

/**
 * TimelineDraftBlock: An interactive placeholder for new events.
 * Supports drag-to-move, edge-resize, and inline title entry.
 * SHADOW VERSION (.tsx)
 */
export const TimelineDraftBlock: React.FC<TimelineDraftBlockProps> = ({
    start,
    end,
    selectedDate,
    hourHeight,
    onUpdate,
    onConfirm,
    onCancel,
    onExpand
}) => {
    const [title, setTitle] = useState<string>('');
    const [interaction, setInteraction] = useState<'MOVE' | 'RESIZE_TOP' | 'RESIZE_BOTTOM' | null>(null);
    
    const blockRef = useRef<HTMLDivElement>(null);
    const interactionState = useRef<{
        startY: number;
        startStartTime: Date | null;
        startEndTime: Date | null;
    }>({
        startY: 0,
        startStartTime: null,
        startEndTime: null
    });

    const top = getPosition(start, selectedDate, hourHeight);
    const bottom = getPosition(end, selectedDate, hourHeight);
    const height = Math.max(30, bottom - top);
    const themeColor = EVENT_THEMES[EVENT_TYPES.PERFORMANCE];

    const handlePointerDown = (e: React.PointerEvent, type: 'MOVE' | 'RESIZE_TOP' | 'RESIZE_BOTTOM') => {
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        
        e.stopPropagation();
        e.preventDefault();
        
        setInteraction(type);
        interactionState.current = {
            startY: e.clientY,
            startStartTime: new Date(start),
            startEndTime: new Date(end)
        };
        
        const target = e.currentTarget as HTMLElement;
        target.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!interaction || !interactionState.current.startStartTime || !interactionState.current.startEndTime) return;

        const deltaY = e.clientY - interactionState.current.startY;
        const deltaMinutes = deltaY / (hourHeight / 60);
        const granularity = getGranularity(hourHeight);
        const snappedDelta = Math.round(deltaMinutes / granularity) * granularity;

        if (interaction === 'MOVE') {
            const newStart = new Date(interactionState.current.startStartTime.getTime() + snappedDelta * 60000);
            const newEnd = new Date(interactionState.current.startEndTime.getTime() + snappedDelta * 60000);
            
            if (newStart.getHours() >= 0 && newEnd.getHours() <= 23 && newEnd.getDate() === newStart.getDate()) {
                onUpdate({ start: newStart, end: newEnd });
            }
        } else if (interaction === 'RESIZE_TOP') {
            const newStart = new Date(interactionState.current.startStartTime.getTime() + snappedDelta * 60000);
            if (newStart.getTime() <= end.getTime() - (granularity * 60000) && newStart.getHours() >= 0) {
                onUpdate({ start: newStart });
            }
        } else if (interaction === 'RESIZE_BOTTOM') {
            const newEnd = new Date(interactionState.current.startEndTime.getTime() + snappedDelta * 60000);
            if (newEnd.getTime() >= start.getTime() + (granularity * 60000) && newEnd.getHours() <= 23) {
                onUpdate({ end: newEnd });
            }
        }
    };

    const handlePointerUp = (e: React.PointerEvent) => {
        if (interaction) {
            setInteraction(null);
            const target = e.currentTarget as HTMLElement;
            target.releasePointerCapture(e.pointerId);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') onConfirm(title);
        if (e.key === 'Escape') onCancel();
    };

    return (
        <div
            ref={blockRef}
            className="draft-block animate-in fade-in duration-300"
            style={{
                position: 'absolute',
                top: 0,
                transform: `translateY(${top}px)`,
                left: 0,
                right: 0,
                height: `${height}px`,
                backgroundColor: `${themeColor}22`,
                border: `2px dashed ${themeColor}`,
                borderRadius: '8px',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                padding: '6px 12px',
                boxSizing: 'border-box',
                cursor: interaction ? 'grabbing' : 'default',
                backdropFilter: 'blur(4px)',
                touchAction: 'none',
                boxShadow: `0 0 20px ${themeColor}11`
            }}
            onPointerDown={(e) => handlePointerDown(e, 'MOVE')}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
        >
            <div 
                onPointerDown={(e) => handlePointerDown(e, 'RESIZE_TOP')}
                style={{ position: 'absolute', top: -15, left: 0, right: 0, height: '30px', cursor: 'ns-resize', zIndex: 1001 }} 
            />
            <div 
                onPointerDown={(e) => handlePointerDown(e, 'RESIZE_BOTTOM')}
                style={{ position: 'absolute', bottom: -15, left: 0, right: 0, height: '30px', cursor: 'ns-resize', zIndex: 1001 }} 
            />

            <div style={{ position: 'absolute', top: 2, left: '50%', transform: 'translateX(-50%)', width: '40px', height: '3px', borderRadius: '2px', backgroundColor: themeColor, opacity: 0.3 }} />
            <div style={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)', width: '40px', height: '3px', borderRadius: '2px', backgroundColor: themeColor, opacity: 0.3 }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', height: '100%' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <input 
                        autoFocus
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onPointerDown={(e) => e.stopPropagation()}
                        placeholder="TAP TO NAME..."
                        style={{
                            all: 'unset',
                            fontSize: height < 50 ? '12px' : '16px',
                            fontWeight: '900',
                            color: '#fff',
                            textTransform: 'uppercase',
                            width: '100%'
                        }}
                    />
                    {height > 40 && (
                        <div style={{ fontSize: '10px', color: themeColor, fontWeight: '900', marginTop: '2px', opacity: 0.8 }}>
                            {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} - {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                        </div>
                    )}
                </div>
                
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button onClick={(e) => { e.stopPropagation(); onExpand(title); }} style={{ all: 'unset', cursor: 'pointer', padding: '6px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '50%' }}>
                        <Edit3 size={18} color="#fff" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onConfirm(title); }} style={{ all: 'unset', cursor: 'pointer', padding: '6px', backgroundColor: '#2ecc71', borderRadius: '50%', boxShadow: '0 4px 10px rgba(46, 204, 113, 0.3)' }}>
                        <Check size={18} color="#000" />
                    </button>
                </div>
            </div>
        </div>
    );
};
